import { http } from '../../services/http';
import type { Grant, Scope } from '../auth/types';

export interface Branch {
  id: string;
  code: string;
  name: string;
  timezone: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  status: 'active' | 'inactive';
}

export interface StaffMember {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  status: 'invited' | 'active' | 'disabled';
  designation: string | null;
  employeeCode: string | null;
  primaryBranch: { id: string; name: string } | null;
  reportsTo: { id: string; fullName: string } | null;
  isAssignable: boolean;
  maxOpenLeads: number | null;
  /** False: non-urgent notifications outside working hours wait until the next working start (Phase 6). */
  afterHoursAlerts?: boolean;
  roles: {
    roleId: string;
    code: string;
    name: string;
    rank: number;
    branch: { id: string; name: string } | null;
  }[];
  teams: { id: string; name: string; role: string }[];
  lastLoginAt: string | null;
}

export interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  rank: number;
  isSystem: boolean;
  memberCount: number;
  permissionCount: number;
}

export interface RoleDetail extends Omit<Role, 'memberCount' | 'permissionCount'> {
  permissions: Record<string, Scope>;
  editable: boolean;
}

export interface PermissionDef {
  code: string;
  module: string;
  action: string;
  description: string;
  allowedScopes: Scope[];
}

export interface Department {
  id: string;
  kind: 'department' | 'team';
  name: string;
  status: string;
  branch: { id: string | null; name: string | null } | null;
  parentId: string | null;
  leader: { id?: string; fullName?: string } | null;
  members: { id?: string; fullName?: string; role: 'member' | 'leader' }[];
}

export interface Override {
  permission: string;
  effect: 'grant' | 'deny';
  scope: Scope | null;
  reason: string | null;
}

export interface ListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  status?: string;
  branchId?: string;
  roleId?: string;
}

export const teamApi = {
  branches: (query: ListQuery = {}) =>
    http.list<Branch[]>('/branches', { query: { pageSize: 100, ...query } }),
  createBranch: (body: Record<string, unknown>) => http.post<Branch>('/branches', body),
  updateBranch: (id: string, body: Record<string, unknown>) => http.patch<Branch>(`/branches/${id}`, body),
  setBranchStatus: (id: string, action: 'activate' | 'deactivate') =>
    http.post<Branch>(`/branches/${id}/${action}`),

  departments: () => http.get<Department[]>('/departments'),
  createDepartment: (body: Record<string, unknown>) => http.post<Department>('/departments', body),
  updateDepartment: (id: string, body: Record<string, unknown>) =>
    http.patch<Department>(`/departments/${id}`, body),
  deleteDepartment: (id: string) => http.delete<void>(`/departments/${id}`),
  setMembers: (id: string, members: { userId: string; role: 'member' | 'leader' }[]) =>
    http.put<Department>(`/departments/${id}/members`, { members }),

  staff: (query: ListQuery = {}) => http.list<StaffMember[]>('/staff', { query: { ...query } }),
  staffMember: (id: string) => http.get<StaffMember>(`/staff/${id}`),
  invite: (body: Record<string, unknown>) =>
    http.post<StaffMember & { inviteEmailSent: boolean }>('/staff', body),
  updateStaff: (id: string, body: Record<string, unknown>) => http.patch<StaffMember>(`/staff/${id}`, body),
  setStaffStatus: (id: string, action: 'deactivate' | 'reactivate') =>
    http.post<StaffMember>(`/staff/${id}/${action}`),
  resendInvite: (id: string) => http.post<{ emailSent: boolean }>(`/staff/${id}/invitation`),
  revokeSessions: (id: string) => http.post<void>(`/staff/${id}/revoke-sessions`),
  setRoles: (id: string, roles: { roleId: string; branchId: string | null }[]) =>
    http.put<StaffMember>(`/staff/${id}/roles`, { roles }),
  overrides: (id: string) => http.get<Override[]>(`/staff/${id}/permission-overrides`),
  setOverrides: (
    id: string,
    overrides: { permission: string; effect: 'grant' | 'deny'; scope?: Scope; reason?: string }[],
  ) => http.put<Override[]>(`/staff/${id}/permission-overrides`, { overrides }),
  effective: (id: string) =>
    http.get<{ rank: number; permissions: Record<string, Grant> }>(`/staff/${id}/effective-permissions`),

  permissions: () => http.get<PermissionDef[]>('/permissions'),
  roles: () => http.get<Role[]>('/roles'),
  role: (id: string) => http.get<RoleDetail>(`/roles/${id}`),
  setRolePermissions: (id: string, permissions: Record<string, Scope>) =>
    http.put<RoleDetail>(`/roles/${id}/permissions`, { permissions }),

  audit: (query: { cursor?: string; action?: string; limit?: number }) =>
    http.list<
      {
        id: string;
        action: string;
        actorType: string;
        actor: { id: string; fullName: string } | null;
        entityType: string | null;
        entityId: string | null;
        before: unknown;
        after: unknown;
        metadata: unknown;
        createdAt: string;
      }[]
    >('/audit-logs', { query }),
};

export const teamKeys = {
  branches: ['team', 'branches'] as const,
  departments: ['team', 'departments'] as const,
  staff: (query: ListQuery) => ['team', 'staff', query] as const,
  staffMember: (id: string) => ['team', 'staff', id] as const,
  overrides: (id: string) => ['team', 'staff', id, 'overrides'] as const,
  roles: ['team', 'roles'] as const,
  role: (id: string) => ['team', 'roles', id] as const,
  permissions: ['team', 'permissions'] as const,
  audit: (action: string) => ['audit', action] as const,
};

export const SCOPE_LABELS: Record<Scope, string> = {
  own: 'Own',
  assigned: 'Assigned',
  team: 'Team',
  branch: 'Branch',
  tenant: 'Everything',
  platform: 'Platform',
};
